import React, { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, Languages, ChevronDown, MessageCircle, User, LogOut, BookOpen, Briefcase, ShieldCheck } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { getPageWaLink } from "../data/firm";
import { resolveWaKey } from "../lib/waKey";
import AdminNotificationBell from "./AdminNotificationBell";
import LawsNavMenu from "./LawsNavMenu";

const PRIMARY_NAV = (t) => [
  { to: "/", label: t.nav.home, end: true },
  { to: "/about", label: t.nav.about },
  { to: "/services", label: t.nav.services },
  { to: "/ai-legal-assistant", label: t.nav.ai },
  { to: "/laws", label: t.nav.laws, kind: "laws-menu" },
  { to: "/search", label: t.nav.search },
  { to: "/blog", label: t.nav.blog || (t.nav.contact === "تواصل" ? "المدونة" : "Blog") },
  { to: "/team", label: t.nav.team },
  { to: "/saved", label: t.nav.saved },
  { to: "/contact", label: t.nav.contact },
];

const navLinkClass = ({ isActive }) =>
  `text-sm whitespace-nowrap transition-colors ${
    isActive ? "font-semibold" : "hover:opacity-80"
  }`;

const NavLinks = ({ onNav }) => {
  const { t } = useI18n();
  return (
    <>
      {PRIMARY_NAV(t).map((it) => {
        if (it.kind === "laws-menu") {
          return <LawsNavMenu key={it.to} />;
        }
        return (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            onClick={onNav}
            data-testid={`nav-link-${it.to.replace("/", "") || "home"}`}
            className={navLinkClass}
            style={({ isActive }) => ({ color: isActive ? "var(--gold-deep)" : "var(--ink)" })}
          >
            {it.label}
          </NavLink>
        );
      })}
    </>
  );
};

const LangToggle = () => {
  const { lang, setLang } = useI18n();
  return (
    <button
      type="button"
      onClick={() => setLang(lang === "en" ? "ar" : "en")}
      data-testid="lang-toggle"
      className="inline-flex items-center gap-1.5 text-sm transition-colors"
      style={{ color: "var(--ink-soft)" }}
    >
      <Languages className="w-4 h-4" />
      <span className="font-medium tracking-wide">{lang === "en" ? "العربية" : "EN"}</span>
    </button>
  );
};

export default function Header() {
  const { t, lang } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  // Per-route WhatsApp link so the header button drops a contextual
  // pre-filled message no matter where the visitor is.
  const headerWa = getPageWaLink(resolveWaKey(pathname), lang);

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl"
            style={{ background: "rgba(252,250,246,0.85)", borderBottom: "1px solid var(--line)" }}>
      <div className="container-luxe h-16 lg:h-20 flex items-center justify-between gap-4">
        <div className="flex items-center gap-5 lg:gap-8 rtl:gap-x-reverse">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3 shrink-0">
            <img
              src="/branding/logo-transparent.png"
              alt="Aldakheel Legal Group"
              className="h-10 lg:h-12 w-auto object-contain"
            />
            <span className="sr-only">Aldakheel Legal Group</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-4 xl:gap-6 rtl:gap-x-reverse">
            <NavLinks />
          </nav>
        </div>

        <div className="flex items-center gap-2 lg:gap-3 rtl:gap-x-reverse md:ps-3 lg:ps-5 md:border-s"
             style={{ borderColor: "var(--line)" }}>
          {user ? (
            <div className="hidden md:flex items-center gap-2 lg:gap-3">
              {user.role === "admin" && (
                <AdminNotificationBell />
              )}
              {user.role === "admin" && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      data-testid="admin-trigger"
                      className="inline-flex items-center gap-1 text-sm hover:opacity-80 outline-none whitespace-nowrap"
                      style={{ color: "var(--ink-soft)" }}
                    >
                      <ShieldCheck className="w-4 h-4" />
                      <span className="hidden lg:inline">{lang === "ar" ? "إدارة" : "Admin"}</span>
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-sm min-w-[210px] mt-2">
                    <DropdownMenuItem asChild><Link to="/admin" data-testid="admin-dashboard-link" className="text-sm cursor-pointer font-medium">{lang === "ar" ? "لوحة التحكم" : "Dashboard"}</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/admin/bookings" data-testid="admin-bookings-link" className="text-sm cursor-pointer">{lang === "ar" ? "الحجوزات (أرشيف)" : "Bookings (Archive)"}</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/admin/contacts" data-testid="admin-contacts-link" className="text-sm cursor-pointer">{lang === "ar" ? "الاستفسارات" : "Inquiries"}</Link></DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild><Link to="/admin/staff" data-testid="admin-staff-link" className="text-sm cursor-pointer">{lang === "ar" ? "الموظفين" : "Staff"}</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/admin/training" data-testid="admin-training-link" className="text-sm cursor-pointer">{lang === "ar" ? "تدريب سامي" : "Train Sami"}</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/admin/laws" data-testid="admin-laws-link" className="text-sm cursor-pointer">{lang === "ar" ? "القوانين" : "Laws"}</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/admin/research" data-testid="admin-research-link" className="text-sm cursor-pointer">{lang === "ar" ? "الأبحاث" : "Research"}</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/admin/judgments" data-testid="admin-judgments-link" className="text-sm cursor-pointer">{lang === "ar" ? "الأحكام" : "Judgments"}</Link></DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* User profile dropdown — consolidates Portal, Cassation, Logout */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    data-testid="user-trigger"
                    className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-sm border transition-colors hover:bg-[var(--beige)] outline-none whitespace-nowrap"
                    style={{ borderColor: "var(--line)", color: "var(--ink)" }}
                  >
                    <span
                      className="w-6 h-6 rounded-full grid place-items-center text-[10px] font-semibold"
                      style={{ background: "var(--gold)", color: "#1a1300" }}
                    >
                      {(user.name || user.email || "?").trim()[0].toUpperCase()}
                    </span>
                    <span className="hidden lg:inline text-sm">
                      {(user.name || "").split(" ")[0] || (lang === "ar" ? "حسابي" : "Account")}
                    </span>
                    <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-sm min-w-[210px] mt-2">
                  <div className="px-3 py-2.5 border-b" style={{ borderColor: "var(--line)" }}>
                    <p className="text-sm font-medium truncate">{user.name || (lang === "ar" ? "مستخدم" : "User")}</p>
                    <p className="text-xs text-slate-500 truncate">{user.email}</p>
                  </div>
                  <DropdownMenuItem asChild>
                    <Link to="/portal" data-testid="portal-link" className="text-sm cursor-pointer">
                      <Briefcase className="w-3.5 h-3.5 me-2 opacity-60" />
                      {t.nav.portal}
                    </Link>
                  </DropdownMenuItem>
                  {(user.role === "staff" || user.role === "admin") && (
                    <DropdownMenuItem asChild>
                      <Link to="/staff/principles" data-testid="staff-principles-link" className="text-sm cursor-pointer">
                        <BookOpen className="w-3.5 h-3.5 me-2 opacity-60" />
                        {lang === "ar" ? "مبادئ التمييز" : "Cassation Principles"}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={async () => { await logout(); navigate("/"); }}
                    data-testid="logout-btn"
                    className="text-sm cursor-pointer text-red-700 focus:text-red-700"
                  >
                    <LogOut className="w-3.5 h-3.5 me-2 opacity-70" />
                    {t.nav.logout}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <Link to="/login" data-testid="login-link"
                  className="hidden md:inline-flex items-center gap-1.5 text-sm hover:opacity-80 whitespace-nowrap shrink-0"
                  style={{ color: "var(--ink-soft)" }}>
              <User className="w-4 h-4" />
              {t.nav.login}
            </Link>
          )}

          <a
            href={headerWa}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-btn"
            className="hidden md:inline-flex items-center justify-center gap-2 h-10 px-3 lg:px-4 rounded-sm text-sm font-medium transition-colors"
            style={{ background: "var(--wa)", color: "#fff" }}
            aria-label={lang === "ar" ? "تواصل واتساب" : "WhatsApp"}
          >
            <MessageCircle className="w-4 h-4" />
            <span className="hidden xl:inline">{lang === "ar" ? "واتساب" : "WhatsApp"}</span>
          </a>

          <Button
            onClick={() => window.open(getPageWaLink("consultation", lang), "_blank", "noopener,noreferrer")}
            data-testid="header-book-btn"
            className="hidden md:inline-flex btn-gold rounded-sm px-3 lg:px-5 h-10 text-sm whitespace-nowrap"
          >
            {t.nav.bookConsult}
          </Button>

          {/* Utility toggle (language) moved to the far end (visual left in RTL). */}
          <span
            className="hidden md:inline-block h-6 w-px"
            style={{ background: "var(--line)" }}
            aria-hidden
          />
          <LangToggle />

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                aria-label="Open menu"
                data-testid="mobile-menu-btn"
                className="lg:hidden p-2 -mr-2 rtl:-ml-2 rtl:mr-0"
              >
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side={lang === "ar" ? "right" : "left"} className="w-80">
              <div className="flex flex-col gap-6 mt-6">
                <NavLinks onNav={() => setOpen(false)} />
                <div className="hairline pt-6 flex flex-col gap-4">
                  {user ? (
                    <>
                      <Link to="/portal" onClick={() => setOpen(false)} className="text-sm">{t.nav.portal}</Link>
                      <button onClick={async () => { await logout(); setOpen(false); navigate("/"); }}
                              className="text-start text-sm">{t.nav.logout}</button>
                    </>
                  ) : (
                    <Link to="/login" onClick={() => setOpen(false)} className="text-sm">{t.nav.login}</Link>
                  )}
                  <a href={headerWa} target="_blank" rel="noopener noreferrer"
                     onClick={() => setOpen(false)}
                     data-testid="mobile-drawer-whatsapp"
                     className="inline-flex items-center justify-center gap-2 h-11 rounded-sm text-sm font-medium"
                     style={{ background: "var(--wa)", color: "#fff" }}>
                    <MessageCircle className="w-4 h-4" />
                    {lang === "ar" ? "تواصل واتساب" : "WhatsApp Us"}
                  </a>
                  <Button onClick={() => { setOpen(false); window.open(getPageWaLink("consultation", lang), "_blank", "noopener,noreferrer"); }}
                          className="btn-gold rounded-sm h-11">
                    {t.nav.bookConsult}
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
